// Tutorial Editor - Tiptap-based Notion-style block editor
// Uses ONLY exports proven to work: useEditor + EditorContent from @tiptap/react
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TextStyle, Color, BackgroundColor } from '@tiptap/extension-text-style';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Code,
  Heading1, Heading2, Heading3, List, ListOrdered,
  Quote, Link as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon,
  AlignLeft, AlignCenter, AlignRight, Highlighter, Undo, Redo,
  Minus, Loader2, Film, Upload, CheckSquare,
  Baseline, PaintBucket, FileCode2,
} from 'lucide-react';
import { toast } from 'sonner';
import { TutorialCodeBlock } from './CodeBlockView';
import { HtmlBlock } from './HtmlBlockView';

// Palettes de la barre d'outils
const TEXT_COLORS = [
  '#0f172a', '#64748b', '#ef4444', '#f97316', '#f59e0b',
  '#10b981', '#0ea5e9', '#4F46E5', '#8b5cf6', '#ec4899',
];

const BG_COLORS = [
  '#fef08a', '#fed7aa', '#fecaca', '#fbcfe8', '#ddd6fe',
  '#bfdbfe', '#a5f3fc', '#bbf7d0', '#d9f99d', '#e2e8f0',
];

export default function TutorialEditor({
  content,
  onUpdate,
  onImageUpload,
  editable = true,
}) {
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTab, setVideoTab] = useState('youtube'); // 'youtube' | 'drive' | 'upload'
  const [uploading, setUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);

  // Use a ref to avoid stale closure in editorProps handlers
  const imageUploadRef = useRef(null);
  imageUploadRef.current = onImageUpload;

  const handleImageFileUploadRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        codeBlock: false,
      }),
      TutorialCodeBlock,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({
        allowBase64: false,
        HTMLAttributes: { class: 'tut-editor-image' },
      }),
      Youtube.configure({
        HTMLAttributes: { class: 'tut-editor-youtube' },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Highlight.configure({ multicolor: true }),
      TextStyle,
      Color,
      BackgroundColor,
      TaskList,
      TaskItem.configure({ nested: true }),
      HtmlBlock,
    ],
    content: content || { type: 'doc', content: [{ type: 'paragraph' }] },
    editable,
    onUpdate: ({ editor }) => {
      if (onUpdate) {
        onUpdate(editor.getJSON());
      }
    },
    editorProps: {
      attributes: {
        class: 'tut-editor-content',
      },
      handleDrop: (view, event, slice, moved) => {
        if (!moved && event.dataTransfer?.files?.length) {
          const file = event.dataTransfer.files[0];
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            if (handleImageFileUploadRef.current) {
              handleImageFileUploadRef.current(file);
            }
            return true;
          }
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (items) {
          for (const item of items) {
            if (item.type.startsWith('image/')) {
              event.preventDefault();
              const file = item.getAsFile();
              if (file && handleImageFileUploadRef.current) {
                handleImageFileUploadRef.current(file);
              }
              return true;
            }
          }
        }
        return false;
      },
    },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      if (!content) {
        // New/empty page — clear editor
        editor.commands.setContent({ type: 'doc', content: [{ type: 'paragraph' }] }, false);
      } else {
        const currentJSON = JSON.stringify(editor.getJSON());
        const newJSON = JSON.stringify(content);
        if (currentJSON !== newJSON) {
          editor.commands.setContent(content, false);
        }
      }
    }
  }, [content, editor]);

  const handleImageFileUpload = useCallback(async (file) => {
    if (!imageUploadRef.current) {
      toast.error('Upload d\'images non configuré');
      return;
    }
    setUploading(true);
    try {
      const url = await imageUploadRef.current(file);
      if (url && editor) {
        editor.chain().focus().setImage({ src: url }).run();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload de l\'image');
    } finally {
      setUploading(false);
    }
  }, [editor]);

  // Keep ref in sync for paste/drop handlers
  handleImageFileUploadRef.current = handleImageFileUpload;

  const handleInsertImageUrl = useCallback(() => {
    if (imageUrl && editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setImageDialogOpen(false);
    }
  }, [editor, imageUrl]);

  const handleInsertYoutube = useCallback(() => {
    if (youtubeUrl && editor) {
      editor.chain().focus().setYoutubeVideo({ src: youtubeUrl }).run();
      setYoutubeUrl('');
      setVideoDialogOpen(false);
    }
  }, [editor, youtubeUrl]);

  // Convert Google Drive sharing link to embeddable URL
  const convertDriveUrl = (url) => {
    // Handle various drive link formats
    const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
    if (fileIdMatch) {
      return `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
    }
    // Handle direct sharing links
    const idMatch = url.match(/[?&]id=([^&]+)/);
    if (idMatch) {
      return `https://drive.google.com/file/d/${idMatch[1]}/preview`;
    }
    return url;
  };

  // Insert Google Drive video
  const handleInsertDriveVideo = useCallback(() => {
    if (videoUrl && editor) {
      const embedUrl = convertDriveUrl(videoUrl);
      // Insert as custom HTML block using a video container
      editor.chain().focus().insertContent({
        type: 'paragraph',
        content: [],
      }).insertContent(`<div class="tut-video-block" data-type="drive"><iframe src="${embedUrl}" width="100%" height="450" frameborder="0" allowfullscreen="true" allow="autoplay"></iframe></div>`).run();
      setVideoUrl('');
      setVideoDialogOpen(false);
    }
  }, [editor, videoUrl]);

  // Handle video file upload
  const handleVideoFileUpload = useCallback(async (file) => {
    if (!file) return;
    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');
      const userId = new URLSearchParams(window.location.search).get('user_id') || '';
      
      // Get user_id from the parent component's props
      const res = await fetch(`${backendUrl}/api/tutorials/upload-video?user_id=${userId}`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: formData,
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur upload vidéo');
      }
      
      const data = await res.json();
      const videoSrc = `${backendUrl}${data.url}`;
      
      if (editor) {
        editor.chain().focus().insertContent(
          `<div class="tut-video-block" data-type="upload"><video controls preload="metadata" src="${videoSrc}" style="width:100%;max-width:100%;border-radius:8px;"></video></div>`
        ).run();
      }
      setVideoDialogOpen(false);
      toast.success('Vidéo insérée');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'upload de la vidéo');
    } finally {
      setVideoUploading(false);
    }
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="tut-editor-wrapper">
      {/* Toolbar */}
      {editable && (
        <div className="tut-toolbar">
          <div className="tut-toolbar-group">
            <TBtn icon={Bold} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Gras" />
            <TBtn icon={Italic} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italique" />
            <TBtn icon={UnderlineIcon} active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Souligné" />
            <TBtn icon={Strikethrough} active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Barré" />
            <TBtn icon={Code} active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Code" />
            <TBtn icon={Highlighter} active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()} title="Surligner" />
            <ColorMenu
              icon={Baseline}
              title="Couleur du texte"
              colors={TEXT_COLORS}
              current={editor.getAttributes('textStyle').color || ''}
              onPick={(c) => editor.chain().focus().setColor(c).run()}
              onClear={() => editor.chain().focus().unsetColor().run()}
            />
            <ColorMenu
              icon={PaintBucket}
              title="Couleur de fond"
              colors={BG_COLORS}
              current={editor.getAttributes('textStyle').backgroundColor || ''}
              onPick={(c) => editor.chain().focus().setBackgroundColor(c).run()}
              onClear={() => editor.chain().focus().unsetBackgroundColor().run()}
            />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={Heading1} active={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Titre 1" />
            <TBtn icon={Heading2} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Titre 2" />
            <TBtn icon={Heading3} active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Titre 3" />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={List} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Liste à puces" />
            <TBtn icon={ListOrdered} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Liste numérotée" />
            <TBtn icon={CheckSquare} active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()} title="Checklist" />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={Quote} active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citation" />
            <TBtn icon={Minus} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Séparateur" />
            <TBtn icon={Code} active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()} title="Bloc de code" />
            <TBtn icon={FileCode2} active={editor.isActive('htmlBlock')} onClick={() => editor.chain().focus().insertHtmlBlock().run()} title="Bloc HTML (publié tel quel)" />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={ImageIcon} onClick={() => setImageDialogOpen(true)} title="Image" />
            <TBtn icon={Film} onClick={() => { setVideoTab('youtube'); setVideoDialogOpen(true); }} title="Vidéo" />
            <TBtn icon={LinkIcon} active={editor.isActive('link')} onClick={() => {
              const url = editor.getAttributes('link').href;
              if (url) { editor.chain().focus().unsetLink().run(); }
              else { const href = window.prompt('URL du lien :'); if (href) editor.chain().focus().setLink({ href }).run(); }
            }} title="Lien" />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={AlignLeft} active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Gauche" />
            <TBtn icon={AlignCenter} active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centre" />
            <TBtn icon={AlignRight} active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Droite" />
          </div>
          <div className="tut-toolbar-divider" />
          <div className="tut-toolbar-group">
            <TBtn icon={Undo} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Annuler" />
            <TBtn icon={Redo} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Rétablir" />
          </div>
        </div>
      )}

      {/* Editor */}
      <div className="tut-editor-area">
        {(uploading || videoUploading) && (
          <div className="tut-upload-overlay">
            <Loader2 className="animate-spin" size={24} />
            <span>{videoUploading ? 'Upload vidéo en cours...' : 'Upload en cours...'}</span>
          </div>
        )}
        <EditorContent editor={editor} />
      </div>

      {/* Image Dialog */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Insérer une image</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">URL de l'image</label>
              <Input placeholder="https://..." value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleInsertImageUrl()} />
            </div>
            <div className="text-center text-sm text-muted-foreground">ou</div>
            <div>
              <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" ref={fileInputRef} className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) { handleImageFileUpload(file); setImageDialogOpen(false); } e.target.value = ''; }} />
              <Button variant="outline" className="w-full" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ImageIcon className="h-4 w-4 mr-2" />}
                Uploader depuis le disque
              </Button>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImageDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleInsertImageUrl} disabled={!imageUrl}>Insérer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Dialog - Multi-tab: YouTube / Google Drive / Upload */}
      <Dialog open={videoDialogOpen} onOpenChange={setVideoDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Film className="h-5 w-5" /> Insérer une vidéo</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${videoTab === 'youtube' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setVideoTab('youtube')}
              >
                <YoutubeIcon className="h-4 w-4 inline mr-1.5" />YouTube
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${videoTab === 'drive' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setVideoTab('drive')}
              >
                <LinkIcon className="h-4 w-4 inline mr-1.5" />Google Drive
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${videoTab === 'upload' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setVideoTab('upload')}
              >
                <Upload className="h-4 w-4 inline mr-1.5" />Upload
              </button>
            </div>

            {/* YouTube Tab */}
            {videoTab === 'youtube' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">URL YouTube</label>
                  <Input
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleInsertYoutube()}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Collez un lien YouTube, la vidéo sera intégrée automatiquement.</p>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setVideoDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleInsertYoutube} disabled={!youtubeUrl}>Insérer</Button>
                </div>
              </div>
            )}

            {/* Google Drive Tab */}
            {videoTab === 'drive' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Lien Google Drive</label>
                  <Input
                    placeholder="https://drive.google.com/file/d/..."
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleInsertDriveVideo()}
                  />
                </div>
                <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">💡 Comment partager depuis Google Drive :</p>
                  <p>1. Ouvrez votre vidéo dans Google Drive</p>
                  <p>2. Clic droit → Partager → Obtenir le lien</p>
                  <p>3. Réglez l'accès sur "Tous les utilisateurs avec le lien"</p>
                  <p>4. Collez le lien ici</p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setVideoDialogOpen(false)}>Annuler</Button>
                  <Button onClick={handleInsertDriveVideo} disabled={!videoUrl}>Insérer</Button>
                </div>
              </div>
            )}

            {/* Upload Tab */}
            {videoTab === 'upload' && (
              <div className="space-y-3">
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/ogg"
                  ref={videoFileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleVideoFileUpload(file);
                    e.target.value = '';
                  }}
                />
                <div
                  className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all"
                  onClick={() => videoFileInputRef.current?.click()}
                >
                  <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium">Cliquez pour sélectionner une vidéo</p>
                  <p className="text-xs text-muted-foreground mt-1">MP4, WebM ou OGG — Max 100 MB</p>
                </div>
                {videoUploading && (
                  <div className="flex items-center justify-center gap-2 text-sm text-primary">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Upload en cours...
                  </div>
                )}
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => setVideoDialogOpen(false)}>Annuler</Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sélecteur de couleur (texte ou fond) : bouton + palette en popover
function ColorMenu({ icon: Icon, title, colors, current, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <div className="tut-color-menu" ref={ref}>
      <button
        type="button"
        className={`tut-toolbar-btn tut-color-trigger ${open ? 'tut-toolbar-btn-active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={title}
      >
        <Icon size={16} />
        <span className="tut-color-dot" style={current ? { background: current } : undefined} />
      </button>

      {open && (
        <div className="tut-color-pop">
          <div className="tut-color-title">{title}</div>
          <div className="tut-color-grid">
            {colors.map((c) => (
              <button
                key={c}
                type="button"
                className={`tut-color-swatch ${current?.toLowerCase() === c.toLowerCase() ? 'is-active' : ''}`}
                style={{ background: c }}
                title={c}
                onClick={() => { onPick(c); setOpen(false); }}
              />
            ))}
          </div>
          <button type="button" className="tut-color-clear" onClick={() => { onClear(); setOpen(false); }}>
            Aucune couleur
          </button>
        </div>
      )}
    </div>
  );
}

function TBtn({ icon: Icon, active, onClick, disabled, title }) {
  return (
    <button type="button" className={`tut-toolbar-btn ${active ? 'tut-toolbar-btn-active' : ''}`} onClick={onClick} disabled={disabled} title={title}>
      <Icon size={16} />
    </button>
  );
}

